import pRetry from 'p-retry';
import { AdditionalInfoFormViewNext } from '../../../types';
import { boloAdditionalInfoToView } from '../../../helpers/bolo-additional-info-to-view';
import { useAdditionalInfosBoloResource } from '../use-additional-infos-bolo-resource';
import { createStreamListener } from './helpers';
import { AdditionalInfosController } from './types';

export interface CreateBoloControllerConfig {
    entityId: number;
    entityNumber: string;
    resource: ReturnType<typeof useAdditionalInfosBoloResource>;
    pageSize: number;
}

/**
 * BOLO controller.
 *
 * BOLOs use entirely different API endpoints (/bolo/additional_info) and a
 * different WebSocket stream ('boloAdditionalInfo'). The `boloAdditionalInfo`
 * listener handles incremental upsert/delete by boloLinkId. The `infos`
 * listener triggers a full page reload on any message — BOLOs don't do
 * selective insert/evict like events.
 */
export function createBoloController(
    config: CreateBoloControllerConfig
): AdditionalInfosController {
    const { entityId, entityNumber, resource, pageSize } = config;

    return {
        async loadPage({ cursor }) {
            return pRetry(
                async () => {
                    if (!entityId) {
                        return {
                            additionalInfos: [],
                            pagination: { hasNextPage: false },
                            agencyEventNumberToIdMap: {},
                        };
                    }

                    const response = await resource.getInfosByCursor({
                        boloId: entityId,
                        limit: pageSize,
                        cursor,
                    });
                    const data = response.data.data;
                    return {
                        additionalInfos:
                            data?.boloAdditionalInfos.map((info) =>
                                boloAdditionalInfoToView(info, entityNumber)
                            ) ?? [],
                        pagination: { hasNextPage: false },
                        agencyEventNumberToIdMap: {},
                    };
                },
                { retries: 2 }
            );
        },

        async createInfo(info: AdditionalInfoFormViewNext) {
            const result = await resource.createAdditionalInfo({
                boloAdditionalInfo: {
                    text: info.text,
                    additionalInfoPriority: info.isHighPriority
                        ? 'HIGH_PRIORITY_UPDATE'
                        : 'STANDARD_UPDATE',
                },
                boloId: entityId,
            });
            const newView = result?.data?.data?.boloAdditionalInfoView?.boloAdditionalInfo;
            return newView ? [boloAdditionalInfoToView(newView, entityNumber)] : [];
        },

        async deleteInfo(infoId: number) {
            await resource.deleteInfo(infoId);
        },

        createStreamListeners({ store, fetchNextPage }) {
            const boloAdditionalInfo = createStreamListener({
                streamNames: ['boloAdditionalInfo'],
                onMessage: (message) => {
                    const { boloId, boloAdditionalInfo, boloLinkId } = message.payload;
                    if (boloId !== entityId) {
                        return;
                    }

                    const isDelete = !boloAdditionalInfo;
                    if (isDelete) {
                        store.removeInfos([boloLinkId]);
                        return;
                    }

                    store.upsertInfos([boloAdditionalInfoToView(boloAdditionalInfo, entityNumber)]);
                },
            });

            const infos = createStreamListener({
                streamNames: ['infos'],
                onMessage: () => {
                    if (entityId) {
                        fetchNextPage({ reset: true });
                    }
                },
            });

            return { infos, boloAdditionalInfo };
        },

        createFilterDirective() {
            return () => true;
        },
    };
}
